import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  RefreshCcw,
  Trash2,
  Heart,
  MapPin,
  ExternalLink,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Favorites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load favorites");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!confirm("Delete this favorite?")) return;
    try {
      await api.delete(`/favorites/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((i) =>
    !q
      ? true
      : (i.name + " " + (i.address || "") + " " + (i.category || ""))
          .toLowerCase()
          .includes(q.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            User data
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Favorites
          </h1>
          <p className="text-sm text-muted-foreground">
            Places saved by users. {items.length} total.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="favorites-refresh-btn"
          className="h-10"
        >
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="mb-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, address or category"
          className="pl-9 h-11"
          data-testid="favorites-search-input"
        />
      </div>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-16 text-center">
          <Heart className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm font-medium">
            {items.length === 0 ? "No favorites yet" : "No matches"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {items.length === 0
              ? "Favorites saved from the mobile app will appear here."
              : "Try a different search term."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
          {filtered.map((f) => (
            <div
              key={f.id}
              data-testid={`favorite-item-${f.id}`}
              className="bg-card border border-border rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-base font-bold truncate">{f.name}</div>
                  {f.category && (
                    <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-md bg-accent text-accent-foreground text-xs font-semibold">
                      {f.category}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del(f.id)}
                  data-testid={`favorite-delete-${f.id}`}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {f.address && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground mb-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span className="truncate">{f.address}</span>
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="mono">
                  {f.latitude.toFixed(4)}, {f.longitude.toFixed(4)}
                </span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${f.latitude},${f.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
